import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const FertiliteNutrition = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Fertilité & nutrition féminine — Nutritionniste Vevey, Clarens & Montreux"
        description="Soutenir la fertilité féminine par la nutrition : accompagnement personnalisé à Vevey, Clarens et Montreux. Pré-conception, équilibre hormonal, SOPK et fertilité. Riviera vaudoise."
        keywords="fertilité nutrition femme, fertilité Vevey, nutrition pré-conception Clarens, accompagnement fertilité Montreux, SOPK fertilité Riviera, nutritionniste fertilité Lausanne, canton de Vaud"
        url="https://nutricentreriviera.ch/fertilite-nutrition-femme"
      />

      {/* Hero */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '5rem 0 4rem',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <span className="caption" style={{
            color: 'var(--accent-primary)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'inline-block',
            marginBottom: '1rem'
          }}>
            Fertilité & nutrition féminine
          </span>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            Fertilité : préparer votre corps pour accueillir la vie
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '780px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
            Désir d'enfant, parcours de PMA, cycles irréguliers, SOPK… La fertilité féminine est profondément liée à votre équilibre hormonal et métabolique. À Vevey, Clarens et Montreux, nous vous accompagnons avec une approche sur mesure pour mettre toutes les chances de votre côté.
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none' }} data-testid="fertilite-hero-cta">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Vous vous reconnaissez si... */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            Vous vous reconnaissez si…
          </h2>
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Le chemin vers la maternité n'est pas toujours linéaire. Ces situations vous parlent ? Vous êtes loin d'être seule.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {[
              "Vous avez un désir d'enfant et souhaitez préparer votre corps en amont.",
              "Vous essayez depuis plusieurs mois sans succès, et vous voulez agir avant d'aller plus loin.",
              "Vos cycles sont irréguliers, douloureux, ou parfois absents.",
              "Vous avez reçu un diagnostic de SOPK ou d'endométriose et savez que la fertilité peut en être affectée.",
              "Vous êtes engagée dans un parcours de PMA et souhaitez optimiser vos chances par la nutrition.",
              "Vous avez arrêté la pilule récemment et votre cycle peine à se remettre en place.",
              "Vous ressentez de la fatigue, des fringales sucrées, une prise de poids — autant de signes d'un déséquilibre hormonal.",
              "Vous voulez aborder ce projet avec sérénité, soutien et savoir-faire."
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '1.1rem 1.25rem',
                background: 'var(--bg-page)',
                borderRadius: '0.85rem',
                border: '1px solid var(--border-light)',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.5rem'
                }} />
                <p className="body-medium" style={{ margin: 0, lineHeight: 1.55 }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi c'est plus complexe */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
            Pourquoi la fertilité féminine est plus complexe qu'il n'y paraît
          </h2>
          <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: 1.7 }}>
            La fertilité ne dépend pas que des organes reproducteurs. C'est l'expression d'un <strong>équilibre global</strong> — hormonal, métabolique, émotionnel, et nutritionnel. Plusieurs leviers peuvent être en jeu :
          </p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              ["L'équilibre œstrogènes / progestérone.", "Indispensable à un cycle ovulatoire régulier et à une bonne implantation."],
              ["La sensibilité à l'insuline et la stabilité glycémique.", "Une résistance à l'insuline, fréquente dans le SOPK, perturbe profondément l'ovulation."],
              ["La fonction thyroïdienne.", "Une thyroïde paresseuse — même infraclinique — peut freiner la fertilité."],
              ["Les micronutriments clés.", "Fer, vitamine D, B9, B12, iode, sélénium, zinc : leurs niveaux conditionnent vos chances de conception."],
              ["L'inflammation et le stress oxydatif.", "Ils altèrent la qualité ovocytaire — souvent davantage que l'âge seul."],
              ["Le poids et la composition corporelle.", "Un déséquilibre — en sous-poids comme en surpoids — peut bloquer l'ovulation."],
              ["L'impact émotionnel.", "Le stress chronique et la charge mentale d'un parcours fertilité influencent directement le cycle."]
            ].map(([t, d], i) => (
              <li key={i} style={{
                background: 'white',
                border: '1px solid var(--border-light)',
                borderLeft: '4px solid var(--accent-primary)',
                borderRadius: '0.75rem',
                padding: '1.1rem 1.4rem'
              }}>
                <p className="body-medium" style={{ marginBottom: '0.35rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t}</p>
                <p className="body-medium" style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.55 }}>{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Notre approche */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Notre approche fertilité à Vevey, Clarens et Montreux
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Notre rôle est de vous accompagner avec rigueur, douceur et discrétion — en complément de votre suivi gynécologique ou de votre parcours de PMA.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              ["Un bilan complet et personnalisé", "Anamnèse approfondie, lecture de vos analyses biologiques, analyse corporelle. Nous identifions vos leviers spécifiques avant toute recommandation."],
              ["Une préparation nutritionnelle ciblée", "Construction d'une stratégie alimentaire qui soutient votre équilibre hormonal, votre cycle et la qualité ovocytaire — sans dogme, sans régime restrictif."],
              ["Un accompagnement bienveillant", "Le parcours fertilité est parfois éprouvant. Nous offrons un espace d'écoute, de soutien et — si vous le souhaitez — d'accompagnement émotionnel via l'hypnopraxie."]
            ].map(([t, d], i) => (
              <div key={i} style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '1.75rem',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem', color: 'var(--accent-primary)' }}>{t}</h3>
                <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--accent-primary)', color: 'white', padding: '5rem 0', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à préparer votre fertilité avec sérénité ?
          </h2>
          <p className="body-large" style={{ color: 'white', opacity: 0.95, marginBottom: '2rem', lineHeight: 1.7 }}>
            Un premier rendez-vous permet d'identifier vos leviers personnels et de construire un accompagnement adapté. Consultations remboursées par certaines assurances complémentaires (ASCA &amp; RME).
          </p>
          <a href={ONEDOC_URL} target="_blank" rel="noopener noreferrer" className="btn-primary"
             style={{ background: 'white', color: 'var(--accent-primary)', textDecoration: 'none' }}
             data-testid="fertilite-final-cta">
            Réserver ma consultation
            <ArrowRight size={20} />
          </a>
          <p className="caption" style={{
            marginTop: '1rem', color: 'white', opacity: 0.9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
          }}>
            <CheckCircle2 size={16} />
            Cabinets à Vevey &amp; Clarens (Montreux) — proche Lausanne
          </p>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['fertilite-nutrition-femme']} />
    </div>
  );
};

export default FertiliteNutrition;
